/**
 * apps/web :: pages :: CheckoutCancelPage
 *
 * `/checkout/cancel` — the page Stripe redirects users to when they
 * back out of the hosted Checkout (FE-02, ROADS-445).
 *
 * Stripe never tells us *why* a user cancelled (cost, wrong tier, just
 * browsing); the copy reflects that uncertainty. No charge has been
 * made — we say so plainly so the user doesn't think we held a
 * reservation.
 */
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";

export default function CheckoutCancelPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <LearnerPortalShell pageTitle={t("checkout.cancel.title")}>
      <section
        data-testid="checkout-cancel-page"
        className="mx-auto max-w-2xl px-md py-lg"
      >
        <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
          {t("checkout.cancel.title")}
        </h1>
        <p className="mt-md text-base text-slate-700">
          {t("checkout.cancel.body")}
        </p>
        <p className="mt-sm text-sm text-slate-500">
          {t("checkout.cancel.subbody")}
        </p>
        <div className="mt-lg flex flex-wrap gap-sm">
          <button
            type="button"
            onClick={() => navigate("/checkout")}
            data-testid="checkout-cancel-pick-different"
            className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl bg-teal-600 px-md text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
          >
            {t("checkout.cancel.pickDifferent")}
          </button>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            data-testid="checkout-cancel-back"
            className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl border border-slate-300 bg-white px-md text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
          >
            <ArrowLeft className="me-xs h-4 w-4" aria-hidden="true" />
            {t("checkout.cancel.back")}
          </button>
        </div>
      </section>
    </LearnerPortalShell>
  );
}
